import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { ConfirmTransactionTemplate } from '@/components/ConfirmTransactionTemplate'
import { ApplePaySafariWarning } from '@/components/ApplePaySafariWarning'
import { useAddMoneyStore } from '@/store/useAddMoneyStore'
import {
  PaymentMethodsType,
  paymentMethodsList,
} from '@/constants/paymentMethods'
import {
  useCalcOnRampCryptoPriceQuotes,
  useCreatePurchaseOnRamp,
  FiatCode,
  CryptoPriceQuotes,
  CreatePurchaseOnRampDTO,
} from '@/domain'
import { useUserStore } from '@/store/useUserStore'
import { useTransactionStore } from '@/store/useTransactionStore'
import { currencyUtils, checkApplePaySupport, isApplePayMethod } from '@/utils'
import { useApplePayRedirect } from '@/hooks'
import type { ApplePayRedirectData } from '@/types/applePay'
import { useAppAnalytics } from '@/services/analytics'

const { parseAmountToFloat, formatFiatCurrency } = currencyUtils

// Find price quotes by payment method
function findPriceQuotesByPaymentMethod(
  paymentMethod: string,
  priceQuotes?: CryptoPriceQuotes[],
): CryptoPriceQuotes | undefined {
  if (!priceQuotes?.length) return undefined

  return priceQuotes.find((quote) => quote.payment_method === paymentMethod)
}

// Get amount based on payment method
function getAmountBasedOnPaymentMethod(
  estimatePriceQuotes?: CryptoPriceQuotes,
  fiatAmount?: { amount: string; code: string },
): string {
  if (estimatePriceQuotes?.fiat_amount?.amount) {
    return estimatePriceQuotes.fiat_amount.amount
  }
  return fiatAmount?.amount || '0'
}

export default function ConfirmAddMoney() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [estimatePriceQuotes, setEstimatePriceQuotes] = useState<
    CryptoPriceQuotes | undefined
  >()
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethodsType | undefined
  >()
  const [showApplePayWarning, setShowApplePayWarning] = useState(false)
  const { trackEvent, events } = useAppAnalytics()

  // Store data
  const { selectedCurrency, fiatAmount, cryptoAmount, amount, paymentMethod } =
    useAddMoneyStore()

  const userAuthenticated = useUserStore((s) => s.userAuthenticated)

  // Apple Pay redirect hook
  const { openApplePayCheckout } = useApplePayRedirect({
    onSuccess: () => {
      // Apple Pay payment successful
    },
    onError: () => {
      // Apple Pay payment failed
    },
    onCancel: () => {
      // Apple Pay payment cancelled
    },
  })

  // Check that required data is present
  useEffect(() => {
    if (!selectedCurrency || !amount || parseFloat(amount) <= 0) {
      navigate('/add-money', {
        replace: true,
        state: { fromConfirm: true },
      })
    }
  }, [selectedCurrency, amount, navigate])

  // Mapping between payment method formats
  const mapPaymentMethodToList = (
    method: string,
  ): PaymentMethodsType | undefined => {
    const mapping: Record<string, string> = {
      'apple-pay': 'ApplePay',
      'google-pay': 'GooglePay',
      card: 'Card',
      'bank-transfer': 'OnlineBanking',
      ApplePay: 'ApplePay',
      GooglePay: 'GooglePay',
      Card: 'Card',
      OnlineBanking: 'OnlineBanking',
    }

    const mappedMethod = mapping[method]
    if (mappedMethod) {
      return paymentMethodsList.find((m) => m.paymentMethod === mappedMethod)
    }
    return undefined
  }

  // Set default payment method
  useEffect(() => {
    if (
      !selectedPaymentMethod &&
      selectedCurrency?.availableOnRampPaymentMethods?.length
    ) {
      // First check if there's a payment method in store (selected on the previous page)
      if (paymentMethod) {
        const methodFromStore = mapPaymentMethodToList(paymentMethod)

        if (methodFromStore) {
          setSelectedPaymentMethod(methodFromStore)
          return
        }
      }

      // If no method from store, select the first available payment method
      // Map available OnRamp methods to PaymentMethodsType format
      for (const onRampMethod of selectedCurrency.availableOnRampPaymentMethods) {
        const mappedMethod = mapPaymentMethodToList(onRampMethod)
        if (mappedMethod) {
          setSelectedPaymentMethod(mappedMethod)
          return
        }
      }

      // If no available methods, set Card as default
      const cardMethod = paymentMethodsList.find(
        (method) => method.paymentMethod === 'Card',
      )
      if (cardMethod) {
        setSelectedPaymentMethod(cardMethod)
      }
    }
  }, [selectedPaymentMethod, selectedCurrency, paymentMethod])

  // Formatted fiat currency amount
  const formattedFiatAmount = formatFiatCurrency({
    value: Number(fiatAmount.amount || 0),
    fiatCode: fiatAmount.code as FiatCode,
    showDecimals: true,
  })

  // API for fetching price quotes
  const { data: estimatePriceQuotesByCurrency, isFetching: isFetchingOnRamp } =
    useCalcOnRampCryptoPriceQuotes(
      {
        fiatCurrency: 'USD' as FiatCode, // TODO: use paymentCurrency from settings
        cryptoCurrency: cryptoAmount.code as any,
        amount: parseAmountToFloat({
          amount: cryptoAmount.amount,
          decimalSeparator: '.',
        }),
      },
      {
        enabled:
          !!selectedCurrency &&
          !!cryptoAmount.amount &&
          parseFloat(cryptoAmount.amount) > 0,
      },
    )

  // Get amount based on selected payment method
  const calculatedAmount = getAmountBasedOnPaymentMethod(
    estimatePriceQuotes,
    fiatAmount,
  )

  // Purchase creation handler
  const handlePurchaseOnRampRedirect = (orderData: CreatePurchaseOnRampDTO) => {
    // Check if we have payment_url or payment_session_token
    if (!orderData.payment_url && !orderData.payment_session_token) {
      alert('Payment initialization failed. Please try again.')
      return
    }

    // Check if Apple Pay is selected
    if (selectedPaymentMethod?.paymentMethod === 'ApplePay') {
      // Prepare Apple Pay redirect data
      const applePayData: ApplePayRedirectData = {
        paymentSessionToken: orderData.payment_session_token || '',
        paymentSessionSecret: orderData.payment_session_secret || '',
        transactionId: orderData.transaction_id,
        orderId: orderData.order_id || orderData.transaction_id,
        amount: fiatAmount.amount,
        currency: fiatAmount.code,
      }

      // Open Apple Pay in new window (Safari on iOS)
      openApplePayCheckout(applePayData)
      return
    }

    // Standard flow for other payment methods
    // Save transaction data to store
    const transactionData = {
      transactionId: orderData.transaction_id,
      paymentUrl: orderData.payment_url,
      paymentSessionToken: orderData.payment_session_token,
      paymentSessionSecret: orderData.payment_session_secret,
    }

    const setLastTransactionData =
      useTransactionStore.getState().setLastTransactionData
    setLastTransactionData(transactionData)

    // Redirect to WebView
    setTimeout(() => {
      navigate('/on-ramp-webview')
    }, 100)
  }

  const { createPurchaseOnRamp, isPending: isCreatingPurchase } =
    useCreatePurchaseOnRamp({
      onSuccess: handlePurchaseOnRampRedirect,
      onError: ({ errorMessage, code }) => {
        trackEvent(events.DEPOSIT_FAILED, {
          error_reason: errorMessage || code || 'unknown',
          amount: parseFloat(fiatAmount.amount || '0'),
          method: selectedPaymentMethod?.paymentMethod || 'unknown',
        })

        // Show toast notification for all errors
        toast.error(errorMessage || t('api:errors.somethingWrong'))
      },
    })

  // Confirmation handler
  function handleConfirmAddMoney() {
    if (
      !estimatePriceQuotes ||
      !selectedPaymentMethod ||
      !userAuthenticated?.user_id
    ) {
      return
    }
    createPurchaseOnRamp({
      userId: userAuthenticated.user_id,
      amount: estimatePriceQuotes.fiat_amount?.amount ?? '',
      cryptoCurrency: estimatePriceQuotes.crypto_amount?.currency as any,
      fiatCurrency:
        (estimatePriceQuotes.fiat_amount?.currency as FiatCode) || 'USD',
      paymentMethod: selectedPaymentMethod.paymentMethod,
      priceQuotesId: estimatePriceQuotesByCurrency?.price_quotes_id,
    })
  }

  // Payment method selection handler
  function handleSelectedPaymentMethod(paymentMethod: PaymentMethodsType) {
    if (estimatePriceQuotesByCurrency?.price_quotes?.length) {
      const priceQuotesByPaymentMethod = findPriceQuotesByPaymentMethod(
        paymentMethod.paymentMethod,
        estimatePriceQuotesByCurrency.price_quotes,
      )

      if (priceQuotesByPaymentMethod) {
        setEstimatePriceQuotes(priceQuotesByPaymentMethod)
      }

      setSelectedPaymentMethod(paymentMethod)

      // Check if Apple Pay is selected in Telegram WebView
      if (isApplePayMethod(paymentMethod)) {
        const applePaySupport = checkApplePaySupport()
        if (applePaySupport.shouldOpenInSafari) {
          setShowApplePayWarning(true)
        } else {
          setShowApplePayWarning(false)
        }
      } else {
        setShowApplePayWarning(false)
      }
    }
  }

  // Set quotes when data is received
  useEffect(() => {
    if (
      estimatePriceQuotesByCurrency?.price_quotes?.length &&
      selectedPaymentMethod
    ) {
      const priceQuotesByPaymentMethod = findPriceQuotesByPaymentMethod(
        selectedPaymentMethod.paymentMethod,
        estimatePriceQuotesByCurrency.price_quotes,
      )

      if (priceQuotesByPaymentMethod) {
        setEstimatePriceQuotes(priceQuotesByPaymentMethod)
      }

      // Check Apple Pay when payment method changes
      if (isApplePayMethod(selectedPaymentMethod)) {
        const applePaySupport = checkApplePaySupport()
        if (applePaySupport.shouldOpenInSafari) {
          setShowApplePayWarning(true)
        } else {
          setShowApplePayWarning(false)
        }
      } else {
        setShowApplePayWarning(false)
      }
    }
  }, [estimatePriceQuotesByCurrency, selectedPaymentMethod])

  if (!selectedCurrency) {
    return null
  }

  return (
    <ConfirmTransactionTemplate
      byScreen="addMoney"
      screenTitle={t('screens:confirmAddMoney.title')}
      buttonTitle={t('form:actions.confirm')}
      isLoading={isFetchingOnRamp}
      onSubmit={handleConfirmAddMoney}
      selectedPaymentMethod={selectedPaymentMethod}
      onSelectPaymentMethod={handleSelectedPaymentMethod}
      currency={selectedCurrency}
      transactionFee={{
        amount: estimatePriceQuotes?.estimated_fiat_fee.amount || '0',
        currency: estimatePriceQuotes?.estimated_fiat_fee.currency || 'USD',
      }}
      amountInCrypto={cryptoAmount.amount}
      total={{
        amount: estimatePriceQuotes?.fiat_amount?.amount ?? calculatedAmount,
        currency: estimatePriceQuotes?.fiat_amount?.currency ?? 'USD',
      }}
      isLoadingSubmit={isCreatingPurchase}
      infoCurrency={`≈${formattedFiatAmount}`}
      _shouldShowOnRampMethods={true}
      _shouldShowWallet={false}
      transactionFeeIsLoading={isFetchingOnRamp}
      // Render Apple Pay warning if needed
      renderBeforeButton={
        showApplePayWarning ? (
          <ApplePaySafariWarning
            onContinue={() => {
              setShowApplePayWarning(false)
            }}
            onCancel={() => {
              // Reset Apple Pay selection and select Card
              const cardMethod = paymentMethodsList.find(
                (m) => m.paymentMethod === 'Card',
              )
              if (cardMethod) {
                handleSelectedPaymentMethod(cardMethod)
              }
              setShowApplePayWarning(false)
            }}
          />
        ) : null
      }
    />
  )
}
